import { difference } from 'lodash';

import type { TraffickingTableName } from '../../../../constants';
import { TRAFFICKING_TABLE_NAMES } from '../../../../constants';
import type { Nullable } from '../../../../models';
import type { UpdatedEntity } from './types';

/**
 * Row-selection helpers for the cascading selection hooks (KATANA-1714).
 *
 * These live here rather than in `hooks/utils.ts` because that module imports
 * `sortOptionMap` from `useTraffickerState`, so anything reading a helper from it joins
 * the cycle around that file (SonarQube `tsarchitecture:S7197`). This module imports only
 * leaf modules, which keeps the two cascade hooks out of that cycle.
 */

/**
 * Identifies which tab the user just added selections to, and which ids it gained.
 *
 * Distinct from getSelectedEntities: that one covers only campaigns and lineItems and
 * reports the whole scope on first match, which cannot express "the user touched the
 * ads tab". Cascading selection needs a single unambiguous origin to radiate from.
 */
export const getSelectionOrigin = (
  scopes: Record<TraffickingTableName, UpdatedEntity>
): Nullable<{ tableName: TraffickingTableName; addedIds: string[] }> => {
  for (const tableName of TRAFFICKING_TABLE_NAMES) {
    const addedIds = difference(scopes[tableName].current, scopes[tableName].previous);

    if (addedIds.length) return { tableName, addedIds };
  }

  return null;
};

/**
 * Mirror of getSelectionOrigin for the other direction: which tab the user just removed
 * selections from, and which ids it lost (KATANA-1714).
 *
 * Kept separate from getDeselectedEntities, which compares lengths rather than membership and
 * only covers campaigns and lineItems, so it cannot express "the user unchecked an ad".
 */
export const getDeselectionOrigin = (
  scopes: Record<TraffickingTableName, UpdatedEntity>
): Nullable<{ tableName: TraffickingTableName; removedIds: string[] }> => {
  for (const tableName of TRAFFICKING_TABLE_NAMES) {
    const removedIds = difference(scopes[tableName].previous, scopes[tableName].current);

    if (removedIds.length) return { tableName, removedIds };
  }

  return null;
};
